/* eslint-disable camelcase */
import { CONFIG } from "~/config";
import { IMantisData, ISubmitData, IPhoto, INewReportData, IUserFeedbackItem } from "./interfaces";
import { DEBUG, SCREENSHOT_DEBUG } from "./const";
import { htmlToImage } from "~/hti";
import { frpc } from "~/util/general";
import { getWasmScreenshot } from "~/util/wasm";

const DEBUG_TIMEOUT = 100;
let isResolve = true;

if (DEBUG) {
	window.setReportResolve = (state: boolean) => {
		isResolve = state;
	};
}

function delayedPromise<T>(data: T): Promise<T> {
	return new Promise((resolve, reject) => {
		setTimeout(() => {
			if (isResolve) {
				resolve(data);
			} else {
				reject(data);
			}
		}, DEBUG_TIMEOUT);
	});
}

export function getMediaIds(photos: Array<IPhoto>): Promise<{
	status: number;
	json: () => Promise<{
		photos: Array<{
			status: number;
			mediaId: number;
		}>;
	}>;
}> {
	if (DEBUG) {
		/* eslint-disable no-console */
		console.log(["getMediaIds", photos]);

		return delayedPromise({
			status: 200,
			json: () => Promise.resolve({
				photos: photos.map(() => ({
					status: 200,
					mediaId: Math.random() * 1000000 >>> 0,
				})),
			}),
		});
	}

	return fetch(
		`${CONFIG.requestUrl}/mediarpc/v1/upload`,
		{
			method: "POST",
			body: JSON.stringify({ photos }),
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json",
			},
			credentials: "include",
		},
	);
}

export function getPhotoId(file: File, onUpdate: (percent: number) => void): Promise<{
	photoId: number;
}> {
	return new Promise((resolve, reject) => {
		if (DEBUG) {
			let ind = 0;
			const max = 10;

			let intId = setInterval(() => {
				const percent = Math.floor(ind / max * 100);

				onUpdate(percent);
				ind++;

				if (ind > max) {
					const photoId = Math.random() * 10e6 >>> 0;

					clearInterval(intId);
					intId = null;

					/* eslint-disable no-console */
					console.log(["getPhotoId", photoId]);

					resolve({
						photoId,
					});
				}
			}, 100);
		} else {
			//provede se upload fotografie - v případě úspěchu získá photoId
			const xhr = new XMLHttpRequest();

			xhr.open("PUT", `${CONFIG.requestUrl}/mediarpc/v1/upload`, true);
			xhr.withCredentials = true;
			xhr.responseType = "json";
			xhr.onload = () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					const { photoId } = xhr.response;

					resolve({
						photoId,
					});
				} else {
					reject({
						photoId: -1,
					});
				}
			};
			xhr.onerror = () => {
				reject({
					photoId: -1,
				});
			};
			// behem uploadu updatujeme progress indikator
			xhr.upload.onprogress = event => {
				const { loaded, total } = event;
				const percent = Math.min(Math.floor(loaded / total * 100), 100);

				onUpdate(percent);
			};
			xhr.send(file);
		}
	});
}

export function getPhotoMediaId({
	file,
	opening = false,
	onUpdate = () => {},
}: {
	file: File | Blob;
	opening?: false;
	onUpdate?: (percent: number) => void;
}): Promise<{
	mediaId: number;
}> {
	return new Promise((resolve, reject) => {
		if (DEBUG) {
			let ind = 0;
			const max = 10;

			let intId = setInterval(() => {
				const percent = Math.floor(ind / max * 100);

				onUpdate(percent);
				ind++;

				if (ind > max) {
					const mediaId = Math.random() * 10e6 >>> 0;

					clearInterval(intId);
					intId = null;

					/* eslint-disable no-console */
					console.log(["getPhotoMediaId", mediaId]);

					resolve({
						mediaId,
					});
				}
			}, 100);
		} else {
			//provede se upload fotografie - v případě úspěchu získá photoId
			const xhr = new XMLHttpRequest();
			const uploadMaxPercent = 25;
			let percent = 0;
			let intId = null;

			const cleartTo = () => {
				intId && clearInterval(intId);
				intId = null;
			};

			xhr.open("POST", `${CONFIG.requestUrl}/mediarpc/v1/media/reserve${opening ? "?no_size_check=1" : ""}`, true);
			xhr.withCredentials = true;
			xhr.responseType = "json";
			xhr.onload = () => {
				onUpdate(100);
				cleartTo();

				if (xhr.status >= 200 && xhr.status < 300) {
					const { mediaId } = xhr.response;

					resolve({
						mediaId,
					});
				} else {
					reject({
						mediaId: -1,
					});
				}
			};
			xhr.onabort = () => {
				cleartTo();
			};
			xhr.onerror = () => {
				cleartTo();
				onUpdate(100);
				reject({
					mediaId: -1,
				});
			};
			// behem uploadu updatujeme progress indikator
			xhr.upload.onprogress = event => {
				const { loaded, total } = event;

				// omezime napr. na n%
				percent = Math.min(Math.floor(loaded / total * uploadMaxPercent), uploadMaxPercent);

				if (percent === uploadMaxPercent && !intId) {
					intId = setInterval(() => {
						percent = Math.min(85, percent + 1);

						if (percent === 85) {
							cleartTo();
						}

						onUpdate(percent);
					}, 250);
				}

				onUpdate(percent);
			};
			xhr.send(file);
		}
	});
}

/*
	string note             Text hlášení, co se zobrazuje v Mantisu
	struct data {           Volitelné parametry:
		string summary          Volitelný sumář hlašení, pokud zůstane prazdný, generuje výchozí
		string email            Volitelný email reportéra, redaktor může na něj poslat z Mantisu odpověď
		string project          Zástupné jméno projektu v Mantisu, do kterého se hlášení přiřadí ('mobil', 'search', 'katalog', ...)
		string category         Jméno kategorie, do které je hlášení v Mantisu přiřazeno (pokud zůstane prazné, bere se z geometrie kresby nebo z konfigu)
		string system           Možnost specifikovat další volitelné hodnoty, podle kterých lze v Mantisu filtrovat (využívají ji JŘ)
		string url              Původní URL při odeslaní hlášení bez schématu a hostu (tj. jen to za lomítkem)
		int zoom                Číslo zoomu na kterém bylo hlašení odesláno
		string detailSource     Aktualně vybraný POI source
		int detailId            POI ID, pokud je vybraná POIka
		string mapSet           Aktualní mapová sada ('zakladni', 'turisticka', ...)
		bool webGL              Příznak, zda měl uživatel v prohlížeči podporu WebGL
		string userAgent        Aktualní user agent (pokud je prázdný, zjistí se hlaviček)
		string host             Aktualní host bez protokolu, (pokud je prazdný, zjistí se hlaviček)
		int rusId               ID aktuálně přihlášeného uživatele
		list attachments [      Seznam příloh (dictu):
			{
				string filename         Jméno přílohy
				binary content          Obsah přílohy
				string mime             Volitelný mimetype přílohy (jinak se odvodí z přípony)
				boolean screenshot      Volitelný příznak, že se jedná o explicitní screenshot obrazovky pořízený z webovky (při propisu hlášení do Mantisu se implicitní screenshot dělat nebude)
			},
			...
		]
		list data [             Čmáranice zakodovaná jako řetězec speciálním kodovacím algoritmem (seznam trojic):
			(char)                  Akce 'd' jako draw, 'e' jako erase
			(int)                   Zoom na kterém byla akce provedena
			(string)                Geometrie zakódovaná do pseudobase64
		]

		photoId te nezajima, to je interni vec pro upload, zajimate te mediaId, coz by se ti nove melo vracet (btw volas tam mediaserver naprimo ze?)
		a pak vezmes pole tech mediaId a nejak ho naformatujes do zpravy toho hlaseni, cili nekam do note
	}
*/
export function sendToMantis(note: string, data: IMantisData, hints?: object): Promise<any> {
	const sendData = [
		note,
		data,
	];

	if (DEBUG) {
		/* eslint-disable no-console */
		//console.log(["send to mantis", note, sendData, hints]);
		console.log(note);
		console.log(data);

		return delayedPromise({});
	}

	return new Promise((resolve, reject) => {
		/* eslint-disable new-cap */
		frpc({
			url: `${CONFIG.requestUrl}/bugreporter`,
			method: "save",
			args: sendData,
			hints,
		}).promise.then(() => {
			resolve({});
		}, error => {
			reject(error);
		});
	});
}

export function getDrawData(reportId: string): Promise<{
	status: number;
	report: {
		scrawl: Array<[string, number, string]>;
	};
}> {
	if (DEBUG) {
		return delayedPromise({
			status: 200,
			statusMessage: "OK",
			report: {
				id: 2081998,
				uuid: "e86c5138-7413-11ed-a6f5-42c67a0052e7",
				state: "processed",
				note: "",
				mantisId: 2037681,
				created: "2022-12-04 21:40:37",
				mapset: "zakladni",
				summary: "",
				email: "",
				project: "",
				category: "",
				zoom: 20,
				url: "/zakladni?pano=1&source=muni&id=959&ds=1&x=13.9126972&y=49.0835211&z=20&base=ophoto",
				host: "mapy.cz",
				poiId: 959,
				poiSource: "muni",
				userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.1 Safari/605.1.15",
				webgl: true,
				version: true,
				system: "",
				point: null,
				scrawl: [
					[
						"d",
						20,
						"9fdmSxTLoyN0N0NAN0NCN0NCN0NDN0NDN0NEM.NIMyNCM.NMMwNDMzNGMzNHMxNMMuNOM9NDM.NPM9NIMwNBM.N2MyNGM.NAMzNEM.NBN0NDM.NBN0NBMzNAN0NAN0NAN0N0N0NAN0N0N0N0N0N0N0N0N0NAM.NAN0N0N0N0N0N0N0N0N0N0M.N0N0N0N0N0N0N0MzN0M.N0M.N0MwN0MzN0MxN0MxN0MzN0MyN0MwN0M.N0MvN0MwN0MxM.MvM.MyN0MvM.MvM.MwM.MwM.MyN0MxMzMwN0MyN0MzM.MyM.MxM.MxN0MzM.MxM.MwN0MyM.MwM.MxN0MuM.MtN0MyN0MxM.MvN0MxN0MtM.MtN0MyM.MmN0MyN0MwM.MsN0MyM.M9N0MzN0MvM.M9N0MzM.MxN0MwN0MzM.MtN0M.M.MxN0M9N0M.N0MxM.M9N0MyN0MvN0M.N0MzM.MxN0MzN0MzN0MzN0MzM.M.N0MyN0M.N0MzN0MzN0M.N0M.N0MzN0M.N0M.M.MzN0N0N0M.N0N0N0N0N0N0N0N0N0N0N0N0M.M.N0N0M.N0N0N0MzN0M.N0MyN0MxN0MyN0M9N0MzN0MlNBMvNBM9NBMtNEM9N2MuN2MoNJMyNBMqNIMxNDMwN2MsNHMxNCMwNDMwNDMuN2MzNCMxNBMxNCMxNAMxNCM.NAMyNAMzNBMzNAMyNAMxNBM.NAMyNAM.N0MzNBMwNAM.N0MyN0M.N0MyNAMzN0M.N0MzN0MzN0M.N0M.N0MzN0M.N0M.N0M.N0M.N0M.N0N0N0M.N0M.N0M.N0M.N0N0N0M.N0N0N0N0N0M.N0N0N0N0N0N0N0N0N0N0N0N0N0N0N0M.N0N0NAN0N0M.N0N0N0N0N0N0N0N0N0N0N0N0NBN0N0N0NAN0NAN0NBN0NDN0NDN0NBNAN2NANINBNENAN4NANGNCN4NBNLNANFNBN4NCNMNAN2NBNHNANHNBNGNCNINAN2NANFNBNHNANANAN2NANDNANDNAN2NANBNANDNAN2NANANBNFNDNENANCNANANDN2N0NBNBNBNCNDN0NBNANDNAN0N0NANANBN0N0N0NBN0N0N0N0N0NAN0N0N0NANAN0N0N0N0NBN0N0N0N0N0NANANAN0N0N0N0N0NBNAN0N0NAN0NANAN0NANBN0NANAN0NANAN0NANAN0N0NBNAN0N0NANAN0N0N0N0NAN0N0N0N0N0N0N0N0N0N0NANBN0N0N0NAN0N0N0NAN0N0N0NCNAN0N0NAN0N0N0N0N0N0N0NBN0N0N0N0N0NAN0N0NANAN0N0N0N0N0NAN0N0N0N0N0N0N0N0N0NBNAN0N0NAN0N0N0NAN0N0N0NBNAN0N0N0N0NAN0N0N0N0N0N0N0N0NANAN0N0N0N0N0N0N0N0",
					],
				],
			},
		});
	}

	return new Promise((resolve, reject) => {
		/* eslint-disable new-cap */
		frpc({
			url: `${CONFIG.requestUrl}/bugreporter`,
			method: "load",
			args: [reportId],
		}).promise.then(data => {
			resolve(data);
		}, error => {
			reject(error);
		});
	});
}

async function screenshotToData(blob: Blob): Promise<ISubmitData> {
	try {
		const binaryData = await Mapy.Util.readBinary(blob);

		return {
			errorMsg: "",
			type: "screenshot",
			screenShot: {
				filename: "screenshot",
				content: binaryData.frpc,
				screenshot: true,
				mime: blob.type,
			},
		};
	} catch (exc) {
		return {
			errorMsg: "Nepodařilo se udělat screenshot!",
			type: "screenshot",
		};
	}
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
	return new Promise(resolve => {
		if (typeof canvas.toBlob === "function") {
			canvas.toBlob((blob: Blob) => resolve(blob));
		}

		return null;
	});
}

interface IGetScreenshotBlob {
	blob: Blob | null;
	error: string;
}

export async function getScreenshotBlob(): Promise<IGetScreenshotBlob> {
	if (SCREENSHOT_DEBUG) {
		const url = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAHElEQVQI12P4//8/w38GIAXDIBKE0DHxgljNBAAO9TXL0Y4OHwAAAABJRU5ErkJggg==";

		try {
			const request = await fetch(url);
			const blob = await request.blob();

			return {
				blob,
				error: "",
			};
		} catch (exc) {
			//
		}
	}

	let wasmImage: HTMLImageElement = null;

	if (CONFIG.wasmEnabled) {
		wasmImage = await getWasmScreenshot();
	}

	try {
		const canvas = await htmlToImage({
			canvas: true,
		}) as HTMLCanvasElement;

		if (typeof canvas.toBlob === "function") {
			const blob = await canvasToBlob(canvas);

			return {
				blob,
				error: "",
			};
		}

		return {
			blob: null,
			error: "Nepodařilo se udělat screenshot!",
		};
	} catch (exc) {
		return {
			blob: null,
			error: "Nepodařilo se udělat screenshot!",
		};
	} finally {
		if (wasmImage) {
			wasmImage.remove();
		}
	}
}

export async function getScreenshot(newType?: boolean): Promise<ISubmitData> {
	try {
		const screenshotBlob = await getScreenshotBlob();

		if (screenshotBlob.error) {
			return {
				errorMsg: screenshotBlob.error,
				type: "screenshot",
			};
		}

		if (newType) {
			return {
				errorMsg: "",
				type: "screenshot",
				screenShot: {
					content: screenshotBlob.blob,
					filename: "",
					mime: "",
					screenshot: true,
				},
			};
		}

		const screenshotData = await screenshotToData(screenshotBlob.blob);

		return screenshotData;
	} catch (exc) {
		/* eslint-disable-next-line */
		console.log(exc);
	}

	return {
		errorMsg: "Nepodařilo se udělat screenshot!",
		type: "screenshot",
	};
}

export async function getScreenshotMediaId(): Promise<ISubmitData> {
	const screenshotData = await getScreenshotBlob();
	let mediaId = 0;

	if (!screenshotData.error) {
		const mediaData = await getPhotoMediaId({
			file: screenshotData.blob,
		});

		mediaId = mediaData.mediaId > 0
			? mediaData.mediaId
			: 0;
	}

	return {
		type: "screenshot",
		mediaId,
		errorMsg: "",
	};
}

export async function getAttachment(attachment: File, newType?: boolean): Promise<ISubmitData> {
	try {
		if (newType) {
			return {
				errorMsg: "",
				type: "attachment",
				attachment: {
					content: attachment,
					filename: "",
					mime: "",
					screenshot: false,
				},
			};
		}

		const binaryData = await Mapy.Util.readBinary(attachment);

		return {
			errorMsg: "",
			type: "attachment",
			attachment: {
				filename: attachment.name,
				content: binaryData.frpc,
				screenshot: false,
				mime: attachment.type,
			},
		};
	} catch (exc) {
		return {
			errorMsg: "Nepodařilo se zpracovat soubory k odeslání!",
			type: "attachment",
		};
	}
}

interface INewReport {
	source?: string;
	id?: number;
	opening?: boolean;
	reportData: INewReportData;
}

export async function newReport({
	reportData,
	source = "",
	id = 0,
	opening = false,
}: INewReport): Promise<{ error: string; uuid: string; }> {
	let url = `${CONFIG.requestUrl}/bugreporter/public/v2/feedback/poi`;

	if (source && id) {
		url = `${CONFIG.requestUrl}/bugreporter/public/v2/feedback/poi/${source}/${id}${opening ? `/opening` : ""}`;
	}

	try {
		const request = await fetch(url, {
			method: "POST",
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify(reportData),
			credentials: "include",
		});
		const response = await request.json();

		if (request.status >= 200 && request.status < 300) {
			return {
				uuid: response.uuid,
				error: "",
			};
		}

		return {
			uuid: "",
			error: `Wrong status code ${request.status}`,
		};
	} catch (exc) {
		return {
			uuid: "",
			error: exc.message || "exception",
		};
	}
}

export async function getUserFeedbacks(): Promise<Array<IUserFeedbackItem>> {
	try {
		const url = `${CONFIG.requestUrl}/bugreporter/public/v2/feedback`;
		const request = await fetch(url, {
			method: "GET",
			credentials: "include",
		});
		const response = await request.json();

		if (request.status === 200) {
			return response;
		}

		return [];
	} catch (exc) {
		return [];
	}
}
